import React from 'react';
import cn from 'classnames';
import {
  ArrowRightIcon,
  ArrowSquareOutIcon,
  CaretDownIcon,
  SquaresFourIcon,
} from '@phosphor-icons/react';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  MPFBlockCardHeader,
  MPFLink,
  MPFSeloGovbr,
} from 'volto-ministerio-publico-federal/components/UI';
import { useMediaQuery } from 'volto-ministerio-publico-federal/hooks/useMediaQuery';
import { useServicos } from 'volto-ministerio-publico-federal/hooks/useServicos';
import {
  descricaoVisivel,
  idDaCategoria,
  type ServicosCategoria,
} from 'volto-ministerio-publico-federal/helpers/Servicos';

import { iconeDaCategoria } from './icones';
import {
  CATALOGO_PADRAO,
  categoriaVisivel,
  linkExtraVisivel,
  niveisPresentes,
  type ServicosCatalogoData,
} from './helpers';

interface ViewProps {
  data: ServicosCatalogoData;
  isEditMode?: boolean;
}

const MOBILE = '(max-width: 767.98px)';

const SetaDoLink: React.FC<{ externo?: boolean }> = ({ externo }) => (
  <span className="mpf-servicos-catalogo__link-seta" aria-hidden="true">
    {externo ? (
      <ArrowSquareOutIcon className="mpf-servicos-catalogo__externo" />
    ) : (
      <ArrowRightIcon />
    )}
  </span>
);

const LinkDaLinha: React.FC<{
  href?: string | null;
  externo?: boolean;
  isEditMode?: boolean;
  className: string;
  children: React.ReactNode;
}> = ({ href, externo, isEditMode, className, children }) =>
  isEditMode ? (
    <span className={className}>{children}</span>
  ) : (
    <MPFLink
      href={href ?? undefined}
      openInNewTab={externo || undefined}
      className={className}
    >
      {children}
    </MPFLink>
  );

interface CardProps {
  categoria: ServicosCategoria;
  acordeao: boolean;
  aberto: boolean;
  onAlternar: () => void;
  isEditMode?: boolean;
}

const Card: React.FC<CardProps> = ({
  categoria,
  acordeao,
  aberto,
  onAlternar,
  isEditMode,
}) => {
  const painelId = `${idDaCategoria(categoria.id)}-painel`;
  const Icone = iconeDaCategoria(categoria.icone);
  const icone = (
    <span className="mpf-servicos-catalogo__icone" aria-hidden="true">
      <Icone />
    </span>
  );

  return (
    <li
      id={idDaCategoria(categoria.id)}
      className={cn('mpf-servicos-catalogo__card', {
        'mpf-servicos-catalogo__card--aberto': !acordeao || aberto,
      })}
    >
      <h3 className="mpf-servicos-catalogo__card-titulo">
        {acordeao ? (
          <button
            type="button"
            className="mpf-servicos-catalogo__alternar"
            aria-expanded={aberto}
            aria-controls={painelId}
            onClick={onAlternar}
          >
            {icone}
            <span className="mpf-servicos-catalogo__card-nome">
              {categoria.titulo}
            </span>
            <CaretDownIcon
              aria-hidden="true"
              className="mpf-servicos-catalogo__seta"
            />
          </button>
        ) : (
          <>
            {icone}
            <span className="mpf-servicos-catalogo__card-nome">
              {categoria.titulo}
            </span>
          </>
        )}
      </h3>

      <div
        id={painelId}
        className="mpf-servicos-catalogo__painel"
        hidden={acordeao && !aberto}
      >
        {descricaoVisivel(categoria) && (
          <p className="mpf-servicos-catalogo__descricao">
            {categoria.descricao}
          </p>
        )}
        {categoria.servicos.length > 0 && (
          <ul className="mpf-servicos-catalogo__servicos">
            {categoria.servicos.map((servico) => (
              <li key={servico.uid} className="mpf-servicos-catalogo__servico">
                <LinkDaLinha
                  href={servico.url}
                  externo={servico.externo}
                  isEditMode={isEditMode}
                  className="mpf-servicos-catalogo__link"
                >
                  <span className="mpf-servicos-catalogo__link-texto">
                    {servico.titulo}
                  </span>
                  {servico.govbr && <MPFSeloGovbr nivel={servico.govbr} />}
                  <SetaDoLink externo={servico.externo} />
                </LinkDaLinha>
              </li>
            ))}
          </ul>
        )}
        {linkExtraVisivel(categoria) && (
          <LinkDaLinha
            href={categoria.link_extra}
            isEditMode={isEditMode}
            className="mpf-servicos-catalogo__link mpf-servicos-catalogo__link--extra"
          >
            <span className="mpf-servicos-catalogo__link-texto">
              {categoria.link_extra_rotulo || categoria.titulo}
            </span>
            <SetaDoLink />
          </LinkDaLinha>
        )}
      </div>
    </li>
  );
};

/**
 * "Todos os serviços": um card por Categoria de Serviço da pasta escolhida,
 * com os serviços dela.
 *
 * Em tela larga é uma grade (3 colunas no desktop, 2 no tablet); no celular,
 * acordeão com o primeiro card aberto. A troca é por `matchMedia` e não só por
 * CSS porque muda a semântica: no acordeão o título do card é um botão com
 * `aria-expanded`; na grade, só texto. No servidor sai a grade, que é também a
 * versão com todo o conteúdo visível.
 */
export const MpfServicosCatalogoBlockView: React.FC<ViewProps> = ({
  data,
  isEditMode,
}) => {
  const tituloId = React.useId();
  const acordeao = useMediaQuery(MOBILE);
  const { categorias, loaded } = useServicos(data?.pasta?.[0]?.['@id']);
  const visiveis = React.useMemo(
    () => categorias.filter(categoriaVisivel),
    [categorias],
  );
  const [abertos, setAbertos] = React.useState<Set<string>>(new Set());
  const iniciou = React.useRef(false);

  React.useEffect(() => {
    if (!iniciou.current && visiveis[0]) {
      iniciou.current = true;
      setAbertos(new Set([visiveis[0].uid]));
    }
  }, [visiveis]);

  const alternar = (uid: string) =>
    setAbertos((atual) => {
      const novo = new Set(atual);
      if (novo.has(uid)) novo.delete(uid);
      else novo.add(uid);
      return novo;
    });

  const niveis = niveisPresentes(visiveis);
  const exibirLegenda = data?.exibirLegenda !== true && niveis.length > 0;
  const titulo = data?.titulo?.trim() || CATALOGO_PADRAO.titulo;

  if (!isEditMode && loaded && visiveis.length === 0) return null;

  return (
    <section
      className="mpf-servicos-catalogo not-prose"
      aria-labelledby={tituloId}
    >
      <MPFBlockCardHeader
        title={titulo}
        titleId={tituloId}
        icon={
          <SquaresFourIcon
            weight="fill"
            aria-hidden="true"
            className="mpf-servicos-catalogo__icone-secao"
          />
        }
        ruleClassName="mpf-servicos-catalogo__regua"
      />

      {isEditMode && !data?.pasta?.[0] && (
        <p className="mpf-servicos-catalogo__aviso">
          Escolha a Pasta de Serviços na barra lateral.
        </p>
      )}
      {isEditMode && data?.pasta?.[0] && loaded && visiveis.length === 0 && (
        <p className="mpf-servicos-catalogo__aviso">
          A pasta não tem categorias com serviços. No site, este bloco não
          aparece.
        </p>
      )}

      {visiveis.length > 0 && (
        <ul className="mpf-servicos-catalogo__grade">
          {visiveis.map((categoria) => (
            <Card
              key={categoria.uid}
              categoria={categoria}
              acordeao={acordeao}
              aberto={abertos.has(categoria.uid)}
              onAlternar={() => alternar(categoria.uid)}
              isEditMode={isEditMode}
            />
          ))}
        </ul>
      )}

      {exibirLegenda && (
        <div className="mpf-servicos-catalogo__legenda">
          <div className="mpf-servicos-catalogo__selos">
            {niveis.map((nivel) => (
              <MPFSeloGovbr key={nivel} nivel={nivel} />
            ))}
          </div>
          <p className="mpf-servicos-catalogo__legenda-texto">
            {data?.legenda ?? CATALOGO_PADRAO.legenda}
          </p>
        </div>
      )}
    </section>
  );
};

export default withBlockExtensions(MpfServicosCatalogoBlockView);
